import React, { useState, useEffect, useCallback } from "react";
import {
  Database,
  Trash2,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Copy,
  CheckCircle,
  Server,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";
const PINECONE_URL = AUTH_URL.replace("/auth", "/pinecone");

const VectorManager = () => {
  const { token } = useAuth();
  const [hosts, setHosts] = useState([]); // All indexes on the Pinecone account
  const [hostsLoading, setHostsLoading] = useState(false);
  const [selectedHost, setSelectedHost] = useState(null); // { name, host, dimension, metric, status }
  const [indexes, setIndexes] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState("");
  const [selectedNamespace, setSelectedNamespace] = useState("");
  const [view, setView] = useState("hosts"); // hosts | namespaces | vectors | metadata

  // Vector search state
  const [vectors, setVectors] = useState([]);
  const [searchFilter, setSearchFilter] = useState({ key: "", value: "" });
  const [vectorCount, setVectorCount] = useState(0);
  const [selectedVectors, setSelectedVectors] = useState(new Set());
  const [expandedVector, setExpandedVector] = useState(null);

  // Metadata state
  const [metadataFields, setMetadataFields] = useState({});
  const [metadataSampled, setMetadataSampled] = useState(0);

  // Confirm state
  const [confirmAction, setConfirmAction] = useState(null);

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  // Fetch all hosts (indexes) on the Pinecone account
  const fetchHosts = useCallback(async () => {
    setHostsLoading(true);
    try {
      const res = await fetch(`${PINECONE_URL}/hosts`, { headers });
      const data = await res.json();
      setHosts(data.indexes || []);
    } catch (error) {
      console.error("Failed to fetch hosts:", error);
    }
    setHostsLoading(false);
  }, [token]);

  // Connect to a specific host — fetch its index stats
  const connectToHost = useCallback(
    async (host) => {
      setSelectedHost(host);
      setLoading(true);
      setView("namespaces");
      try {
        const res = await fetch(
          `${PINECONE_URL}/indexes?host=${encodeURIComponent(host.host)}&index=${encodeURIComponent(host.name)}`,
          { headers },
        );
        const data = await res.json();
        setIndexes(data);
        setSelectedIndex(host.name);
      } catch (error) {
        console.error("Failed to connect to host:", error);
      }
      setLoading(false);
    },
    [token],
  );

  const fetchIndexes = useCallback(async () => {
    if (!selectedHost) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${PINECONE_URL}/indexes?host=${encodeURIComponent(selectedHost.host)}&index=${encodeURIComponent(selectedHost.name)}`,
        { headers },
      );
      const data = await res.json();
      setIndexes(data);
      if (!selectedIndex && Object.keys(data).length > 0) {
        setSelectedIndex(Object.keys(data)[0]);
      }
    } catch (error) {
      console.error("Failed to fetch indexes:", error);
    }
    setLoading(false);
  }, [token, selectedHost]);

  useEffect(() => {
    fetchHosts();
  }, [fetchHosts]);

  const getNamespaces = () => {
    const idx = indexes[selectedIndex];
    if (!idx || !idx.namespaces) return [];
    return Object.entries(idx.namespaces)
      .map(([name, info]) => ({ name, vectorCount: info.vectorCount || 0 }))
      .sort((a, b) => b.vectorCount - a.vectorCount);
  };

  // Helper: get index identifier (host URL if connected via host, otherwise name)
  const getIndexId = () => (selectedHost ? selectedHost.host : selectedIndex);

  const handleDeleteNamespace = async () => {
    if (!confirmAction) return;
    setLoading(true);
    try {
      await fetch(`${PINECONE_URL}/delete-namespace`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          index: getIndexId(),
          namespace: confirmAction.namespace,
        }),
      });
      setConfirmAction(null);
      fetchIndexes();
      if (selectedNamespace === confirmAction.namespace) {
        setSelectedNamespace("");
        setView("namespaces");
      }
    } catch (error) {
      console.error("Delete namespace failed:", error);
    }
    setLoading(false);
  };

  const searchVectors = async (filterOverride) => {
    setLoading(true);
    try {
      const filter = filterOverride || {};
      if (!filterOverride && searchFilter.key && searchFilter.value) {
        // Try to parse value as number or boolean
        let val = searchFilter.value;
        if (val === "true") val = true;
        else if (val === "false") val = false;
        else if (!isNaN(val) && val !== "") val = Number(val);
        filter[searchFilter.key] = val;
      }

      const res = await fetch(`${PINECONE_URL}/query`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          index: getIndexId(),
          namespace: selectedNamespace,
          topK: 100,
          filter: Object.keys(filter).length > 0 ? filter : undefined,
          includeMetadata: true,
          includeValues: false,
        }),
      });
      const data = await res.json();
      const matches = data.matches || [];
      setVectors(matches);
      setVectorCount(matches.length);
      setSelectedVectors(new Set());
    } catch (error) {
      console.error("Search failed:", error);
    }
    setLoading(false);
  };

  const handleDeleteVectors = async (ids) => {
    if (
      !window.confirm(`Delete ${ids.length} vector(s)? This cannot be undone.`)
    )
      return;
    setLoading(true);
    try {
      await fetch(`${PINECONE_URL}/delete-vectors`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          index: getIndexId(),
          namespace: selectedNamespace,
          ids,
        }),
      });
      searchVectors();
      fetchIndexes();
    } catch (error) {
      console.error("Delete vectors failed:", error);
    }
    setLoading(false);
  };

  const handleDeleteByFilter = async () => {
    if (!searchFilter.key || !searchFilter.value) {
      alert("Set a metadata filter first");
      return;
    }
    const count = vectors.length;
    if (
      !window.confirm(
        `Delete all vectors matching ${searchFilter.key}="${searchFilter.value}" in namespace "${selectedNamespace}"? Found ${count} vectors. This cannot be undone.`,
      )
    )
      return;
    setLoading(true);
    try {
      let val = searchFilter.value;
      if (val === "true") val = true;
      else if (val === "false") val = false;
      else if (!isNaN(val) && val !== "") val = Number(val);

      await fetch(`${PINECONE_URL}/delete-vectors`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          index: getIndexId(),
          namespace: selectedNamespace,
          filter: { [searchFilter.key]: val },
        }),
      });
      searchVectors();
      fetchIndexes();
    } catch (error) {
      console.error("Delete by filter failed:", error);
    }
    setLoading(false);
  };

  const fetchMetadata = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${PINECONE_URL}/list-metadata`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          index: getIndexId(),
          namespace: selectedNamespace,
          sampleSize: 500,
        }),
      });
      const data = await res.json();
      setMetadataFields(data.metadata_fields || {});
      setMetadataSampled(data.vectors_sampled || 0);
    } catch (error) {
      console.error("Fetch metadata failed:", error);
    }
    setLoading(false);
  };

  const openNamespace = (ns) => {
    setSelectedNamespace(ns);
    setView("vectors");
    setVectors([]);
    setSearchFilter({ key: "", value: "" });
  };

  const toggleSelectVector = (id) => {
    setSelectedVectors((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedVectors.size === vectors.length) setSelectedVectors(new Set());
    else setSelectedVectors(new Set(vectors.map((v) => v.id)));
  };

  const CopyId = ({ value }) => {
    const [copied, setCopied] = useState(false);
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="p-0.5 text-slate-400 hover:text-blue-600"
        title="Copy ID"
      >
        {copied ? (
          <CheckCircle className="w-3 h-3 text-green-500" />
        ) : (
          <Copy className="w-3 h-3" />
        )}
      </button>
    );
  };

  // ========== NAMESPACE VIEW ==========
  const renderNamespaces = () => {
    const namespaces = getNamespaces();
    const idx = indexes[selectedIndex] || {};

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-800">
              {idx.totalVectors?.toLocaleString() || 0}
            </div>
            <div className="text-xs text-blue-600">Total Vectors</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-slate-800">
              {namespaces.length}
            </div>
            <div className="text-xs text-slate-500">Namespaces</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-slate-800">
              {idx.dimension || "-"}
            </div>
            <div className="text-xs text-slate-500">Dimensions</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                  Namespace
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                  Vectors
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {namespaces.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="px-6 py-4 text-center text-slate-500"
                  >
                    No namespaces found in this index.
                  </td>
                </tr>
              ) : (
                namespaces.map((ns) => (
                  <tr key={ns.name} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <button
                        onClick={() => openNamespace(ns.name)}
                        className="text-sm font-mono text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {ns.name || "(default)"}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {ns.vectorCount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openNamespace(ns.name)}
                          className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                        >
                          Browse
                        </button>
                        <button
                          onClick={() => {
                            setSelectedNamespace(ns.name);
                            setView("metadata");
                            fetchMetadata();
                          }}
                          className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 rounded"
                        >
                          Metadata
                        </button>
                        <button
                          onClick={() =>
                            setConfirmAction({
                              type: "deleteNamespace",
                              namespace: ns.name,
                              count: ns.vectorCount,
                            })
                          }
                          className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Delete confirmation modal */}
        {confirmAction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-500 mr-2" />
                <h3 className="text-lg font-bold text-slate-900">
                  Delete Namespace
                </h3>
              </div>
              <p className="text-sm text-slate-600 mb-2">
                This will permanently delete{" "}
                <strong>
                  all {confirmAction.count?.toLocaleString()} vectors
                </strong>{" "}
                in namespace:
              </p>
              <p className="font-mono text-sm bg-red-50 px-3 py-2 rounded border border-red-200 text-red-800 mb-4">
                {confirmAction.namespace}
              </p>
              <p className="text-xs text-red-600 mb-4">
                This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setConfirmAction(null)}
                  className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg border"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteNamespace}
                  className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg"
                >
                  {loading ? "Deleting..." : "Delete Namespace"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ========== VECTOR BROWSER VIEW ==========
  const renderVectors = () => (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <button
          onClick={() => {
            setView("namespaces");
            setVectors([]);
          }}
          className="text-sm text-slate-500 hover:text-slate-700"
        >
          Namespaces
        </button>
        <span className="text-slate-400">/</span>
        <span className="text-sm font-mono font-medium text-slate-800">
          {selectedNamespace || "(default)"}
        </span>
      </div>

      {/* Search bar */}
      <div className="bg-white rounded-lg shadow-md border border-slate-200 p-4">
        <div className="flex items-end space-x-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Metadata Key
            </label>
            <input
              type="text"
              value={searchFilter.key}
              onChange={(e) =>
                setSearchFilter({ ...searchFilter, key: e.target.value })
              }
              placeholder="e.g. doc_type, category, facility_id"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Value
            </label>
            <input
              type="text"
              value={searchFilter.value}
              onChange={(e) =>
                setSearchFilter({ ...searchFilter, value: e.target.value })
              }
              placeholder="e.g. faq, healthcare, true"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => searchVectors()}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm flex items-center"
          >
            <Search className="w-4 h-4 mr-1" /> Search
          </button>
          <button
            onClick={() => {
              setSearchFilter({ key: "", value: "" });
              searchVectors({});
            }}
            disabled={loading}
            className="px-4 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> All
          </button>
        </div>
      </div>

      {/* Bulk actions */}
      {vectors.length > 0 && (
        <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2 border border-slate-200">
          <div className="flex items-center space-x-3">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={
                  selectedVectors.size === vectors.length && vectors.length > 0
                }
                onChange={toggleSelectAll}
                className="h-4 w-4 text-blue-600 rounded border-slate-300"
              />
              <span className="text-xs text-slate-600">
                {selectedVectors.size} of {vectorCount} selected
              </span>
            </label>
          </div>
          <div className="flex space-x-2">
            {selectedVectors.size > 0 && (
              <button
                onClick={() => handleDeleteVectors([...selectedVectors])}
                className="px-3 py-1 text-xs text-red-600 hover:bg-red-50 rounded border border-red-200"
              >
                Delete Selected ({selectedVectors.size})
              </button>
            )}
            {searchFilter.key && searchFilter.value && (
              <button
                onClick={handleDeleteByFilter}
                className="px-3 py-1 text-xs text-red-600 hover:bg-red-50 rounded border border-red-200"
              >
                Delete All Matching Filter
              </button>
            )}
          </div>
        </div>
      )}

      {/* Results */}
      <div className="bg-white rounded-lg shadow-md border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 w-8"></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Vector ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Score
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Key Metadata
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {vectors.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-8 text-center text-slate-500"
                >
                  {loading
                    ? "Searching..."
                    : 'Click "All" to load vectors, or search by metadata.'}
                </td>
              </tr>
            ) : (
              vectors.map((v) => {
                const meta = v.metadata || {};
                const isExpanded = expandedVector === v.id;
                return (
                  <React.Fragment key={v.id}>
                    <tr
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() =>
                        setExpandedVector(isExpanded ? null : v.id)
                      }
                    >
                      <td
                        className="px-4 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedVectors.has(v.id)}
                          onChange={() => toggleSelectVector(v.id)}
                          className="h-4 w-4 text-blue-600 rounded border-slate-300"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-1">
                          <span className="text-xs font-mono text-slate-700 max-w-xs truncate">
                            {v.id}
                          </span>
                          <CopyId value={v.id} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {v.score?.toFixed(4) || "-"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {meta.doc_type && (
                            <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                              {meta.doc_type}
                            </span>
                          )}
                          {meta.category && (
                            <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-700 rounded">
                              {meta.category}
                            </span>
                          )}
                          {meta.topic && (
                            <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 rounded">
                              {meta.topic}
                            </span>
                          )}
                          {meta.facility_id && (
                            <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                              {meta.facility_id}
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        className="px-4 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex space-x-1">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          )}
                          <button
                            onClick={() => handleDeleteVectors([v.id])}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-6 py-4 bg-slate-50 border-t border-slate-100"
                        >
                          <h4 className="text-xs font-medium text-slate-500 mb-2 uppercase">
                            All Metadata
                          </h4>
                          <div className="grid grid-cols-2 gap-2">
                            {Object.entries(meta)
                              .filter(([k]) => k !== "text")
                              .sort(([a], [b]) => a.localeCompare(b))
                              .map(([key, value]) => (
                                <div key={key} className="flex text-xs">
                                  <span className="font-medium text-slate-600 w-40 flex-shrink-0">
                                    {key}:
                                  </span>
                                  <span className="text-slate-500 truncate max-w-xs">
                                    {typeof value === "object"
                                      ? JSON.stringify(value)
                                      : String(value)}
                                  </span>
                                </div>
                              ))}
                          </div>
                          {meta.text && (
                            <div className="mt-3">
                              <span className="text-xs font-medium text-slate-600">
                                text:
                              </span>
                              <p className="text-xs text-slate-500 mt-1 bg-white p-2 rounded border max-h-32 overflow-y-auto">
                                {meta.text}
                              </p>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ========== METADATA VIEW ==========
  const renderMetadata = () => (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <button
          onClick={() => setView("namespaces")}
          className="text-sm text-slate-500 hover:text-slate-700"
        >
          All Namespaces
        </button>
        <span className="text-slate-400">/</span>
        <span className="text-sm font-mono font-medium text-slate-800">
          {selectedNamespace || "(default)"}
        </span>
        <span className="text-slate-400">/</span>
        <span className="text-sm text-slate-600">Metadata</span>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
        Sampled {metadataSampled} vectors to detect metadata fields and value
        distributions.
      </div>

      <div className="bg-white rounded-lg shadow-md border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Field
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Coverage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Unique Values
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Samples
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {Object.keys(metadataFields).length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  {loading ? "Loading metadata..." : "No metadata found."}
                </td>
              </tr>
            ) : (
              Object.entries(metadataFields)
                .sort(([, a], [, b]) => b.count - a.count)
                .map(([field, info]) => (
                  <tr key={field} className="hover:bg-slate-50">
                    <td className="px-6 py-3 text-sm font-mono font-medium text-slate-800">
                      {field}
                    </td>
                    <td className="px-6 py-3">
                      <span className="px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-600">
                        {info.type}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-24 bg-slate-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${info.coverage}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500">
                          {info.coverage}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-sm text-slate-600">
                      {info.unique_count}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(info.sample_values || [])
                          .slice(0, 3)
                          .map((val, i) => (
                            <span
                              key={i}
                              className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 rounded max-w-xs truncate"
                            >
                              {String(val).substring(0, 50)}
                            </span>
                          ))}
                        {(info.sample_values || []).length > 3 && (
                          <span className="text-xs text-slate-400">
                            +{info.sample_values.length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ========== HOSTS VIEW ==========
  const renderHosts = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
        Showing all indexes on the Pinecone account. Select one to connect and
        browse its namespaces.
      </div>

      <div className="bg-white rounded-lg shadow-md border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Index Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Host
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Dimension
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Metric
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {hostsLoading ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-8 text-center text-slate-500"
                >
                  Loading indexes...
                </td>
              </tr>
            ) : hosts.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-8 text-center text-slate-500"
                >
                  No indexes found on this account.
                </td>
              </tr>
            ) : (
              hosts.map((h) => (
                <tr
                  key={h.name}
                  className={`hover:bg-slate-50 ${selectedHost?.name === h.name ? "bg-blue-50" : ""}`}
                >
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-slate-800">
                      {h.name}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-xs font-mono text-slate-500 max-w-xs truncate block">
                      {h.host}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {h.dimension || "-"}
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-600">
                      {h.metric || "-"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {h.status ? (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                        Ready
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">
                        Not Ready
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => connectToHost(h)}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                    >
                      Connect
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Navigation header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            {view === "hosts" ? (
              <>
                <Server className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-slate-800">
                  Pinecone Indexes
                </span>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    setView("hosts");
                    setSelectedHost(null);
                    setIndexes({});
                    setVectors([]);
                    setSelectedNamespace("");
                  }}
                  className="flex items-center space-x-1 text-sm text-slate-500 hover:text-blue-600"
                >
                  <Server className="w-4 h-4" />
                  <span>All Indexes</span>
                </button>
                <span className="text-slate-400">/</span>
                <div className="flex items-center space-x-1">
                  <Database className="w-4 h-4 text-blue-600" />
                  <button
                    onClick={() => {
                      setView("namespaces");
                      setVectors([]);
                      setSelectedNamespace("");
                    }}
                    className="text-sm font-medium text-blue-700 hover:text-blue-800"
                  >
                    {selectedHost?.name || selectedIndex}
                  </button>
                </div>
              </>
            )}
          </div>
          {view !== "hosts" && view !== "namespaces" && (
            <div className="flex space-x-1 bg-slate-100 rounded-lg p-0.5">
              <button
                onClick={() => {
                  setView("vectors");
                  setVectors([]);
                }}
                className={`px-3 py-1 text-xs rounded-md ${view === "vectors" ? "bg-white shadow text-blue-700" : "text-slate-600"}`}
              >
                Vectors
              </button>
              <button
                onClick={() => {
                  setView("metadata");
                  fetchMetadata();
                }}
                className={`px-3 py-1 text-xs rounded-md ${view === "metadata" ? "bg-white shadow text-blue-700" : "text-slate-600"}`}
              >
                Metadata
              </button>
            </div>
          )}
        </div>
        <button
          onClick={view === "hosts" ? fetchHosts : fetchIndexes}
          disabled={loading || hostsLoading}
          className="px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm flex items-center"
        >
          <RefreshCw
            className={`w-4 h-4 mr-1 ${loading || hostsLoading ? "animate-spin" : ""}`}
          />{" "}
          Refresh
        </button>
      </div>

      {view === "hosts" && renderHosts()}
      {view === "namespaces" && renderNamespaces()}
      {view === "vectors" && renderVectors()}
      {view === "metadata" && renderMetadata()}
    </div>
  );
};

export default VectorManager;
